import { DiContainer, Injectable } from '@jkhy/frontend-utils/di';
import {
    ApprovalListRequestModelDto,
    UserDto,
    UserTimeAccess2Dto,
    UsersClient,
} from '@treasury/api/channel';
import { RequestParams } from '@treasury/api/channel/generated/http-client';
import { AnalyticsService } from '@treasury/core/analytics/analytics.service';
import { AnalyticsEvent } from '@treasury/core/analytics/analytics.types';
import UserRequests from '../../requests/users/users.js';

type ApprovalListRequestModel = Partial<ApprovalListRequestModelDto>;

@Injectable()
export class UsersService {
    constructor(private usersClient: UsersClient) {}

    public static async fetchCurrentUser() {
        return UserRequests.fetchCurrentUser();
    }

    public static async fetchApprovers(body: ApprovalListRequestModel) {
        return UserRequests.fetchApprovers(body);
    }

    public static async fetchAvailableApprovers(body: ApprovalListRequestModel) {
        return UserRequests.fetchAvailableApprovers(body);
    }

    public static async fetchAvailableAndCompletedApprovers(body: ApprovalListRequestModel) {
        return UserRequests.fetchAvailableAndCompletedApprovers(body);
    }

    public static async getPendingApprovalUsers() {
        const usersList: UserDto[] = await UserRequests.getAllUsers();
        return usersList.filter(user => user.publishState === 'Pending');
    }

    public static async approveOrRejectUser(
        user: UserDto,
        action: 'approve' | 'reject',
        comments: string
    ) {
        const response = await UserRequests.approveOrRejectUser(user.id ?? 0, action, comments);

        if (action === 'approve') {
            const di = await DiContainer.getInstance();
            const analytics = await di.getAsync(AnalyticsService);
            analytics.track(AnalyticsEvent.UserApproved, {
                id: (user.id ?? 0).toString(),
            });
        }

        return response;
    }

    public static async searchUsers(filterPredicate: (user: UserDto) => boolean) {
        return UserRequests.searchUsers().then((result: UserDto[]) => {
            if (Array.isArray(result)) {
                return result.filter((user: UserDto) => filterPredicate(user));
            }
            return result;
        });
    }

    public static async getCurrentUserTimeAccess(): Promise<UserTimeAccess2Dto> {
        return UserRequests.getCurrentUserTimeAccess();
    }

    public static async getUserHeader(id: string) {
        return UserRequests.getUserHeader(id);
    }

    public static async getUserInformation(id: string) {
        return UserRequests.getUserInformation(id);
    }

    public static async getUserProductFeatures(id: string) {
        return UserRequests.getUserProductFeatures(id);
    }

    public static async getUserProductFeatureDetail(id: string, featureName: string) {
        return UserRequests.getUserProductFeatureDetail(id, featureName);
    }

    public async fetchAvailableAndCompletedApprovers(body: ApprovalListRequestModel) {
        return UserRequests.fetchAvailableAndCompletedApprovers(body);
    }

    public async getApprovers(request: ApprovalListRequestModelDto, params: RequestParams = {}) {
        return this.usersClient.usersGetApprovers(request, params);
    }

    public async searchUsersNew(
        activeUsersOnly: boolean,
        enrolledUsersOnly: boolean,
        includeRoles: boolean
    ) {
        const response = await this.usersClient.usersSearchUsers({
            activeUsersOnly,
            enrolledUsersOnly,
            includeRoles,
        });

        return response.data;
    }

    /**
     * Resets the password for a digital user by generating a reset password link.
     *
     * @param uniqueUserId - The unique identifier of the  digital user whose password needs to be reset.
     * @returns A promise that resolves to the response data containing the reset password link.
     */
    public async resetPasswordForDigitalUser(uniqueUserId: string) {
        const resp = await this.usersClient.usersResetPasswordLink(uniqueUserId);
        return resp.data;
    }
}
